import type { Block } from '../GridDndEditor/models/Block.model.ts';
import type { GridAddedSocketResponseType } from '../grid/reduxStore/saveHandlers.ts';
import { useAppDispatch } from '../grid/reduxStore/Store.ts';
import { useCurrentUser } from '../../../providers/UserProvider.tsx';
import { useSocketClient } from '../../../providers/SocketContext.tsx';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion.ts';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice.ts';
import { addGridBlockState } from '../grid/reduxStore/editorSlice.ts';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus.ts';
import { AcknowledgmentResponseStatus, BlockEvents, ErrorCode } from '../../../services/socket/SocketEvents.tsx';
import { GridBlockType, type BlockStyles } from 'services/entities/BlockTypes';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { CreateBlockData } from '../UndoRedo/CreateBlockCommand.ts';
import { CreateBlockCommand } from '../UndoRedo/CreateBlockCommand.ts';
import { useSignaturesContext } from '../providers/SignaturesProvider.tsx';
import { updateBlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { normalizeRotation } from '../GridDndEditor/helpers/rotation.ts';

export interface BlockAddedHandlerType {
  (sectionId: string, blockData: CreateBlockData, blockConfig: Block, blockType: GridBlockType): Promise<GridAddedSocketResponseType>;
}

/**
 * Hook for adding blocks with full undo/redo support
 * Creates a CreateBlockCommand and pushes it to the undo stack
 */
export function useNewBlockAddedHandler(): BlockAddedHandlerType {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const { data } = useCurrentUser();
  const userId = data.id;
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const { getSignaturesBySectionId } = useSignaturesContext();

  return useCallback(
    async (
      sectionId: string,
      blockData: CreateBlockData,
      blockConfig: Block,
      blockType: GridBlockType
    ): Promise<GridAddedSocketResponseType> => {
      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();

      const blockId = uuidv4();
      const updatedBlockData: CreateBlockData = {
        ...blockData,
        ...(blockData.styles && {
          styles: {
            ...blockData.styles,
            blockId,
            updatedByUserId: userId,
            currentTimestamp: new Date().toISOString(),
          },
        }),
      };
      const command = new CreateBlockCommand(sectionId, blockId, blockConfig, blockType, updatedBlockData, {
        dispatch,
        socketClient,
        userId,
        checkDocumentLockStatus,
        getSignaturesBySectionId,
      });

      const success = await command.redo();

      if (success) {
        undoRedoManager.pushUndoRedoCommands(command);

        return {
          status: AcknowledgmentResponseStatus.OK,
          content: {
            blockId: command.getCurrentBlockId(),
          },
        } as GridAddedSocketResponseType;
      }

      return {
        status: AcknowledgmentResponseStatus.FAILURE,
        errorCode: ErrorCode.LOCKED,
        content: {
          blockId,
        },
      } as GridAddedSocketResponseType;
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus, getSignaturesBySectionId]
  );
}

/**
 * Hook for adding blocks WITHOUT undo/redo support
 * Used internally by other commands (e.g., DeleteBlockCommand.undo())
 */
export function useBlockAddedHandlerWithoutUndoRedo(): BlockAddedHandlerType {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const { data } = useCurrentUser();
  const userId = data.id;
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    async (
      sectionId: string,
      blockData: CreateBlockData,
      blockConfig: Block,
      blockType: GridBlockType
    ): Promise<GridAddedSocketResponseType> => {
      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const isTable = blockType === GridBlockType.TABLE;

      const payload = {
        blockId: uuidv4(),
        sectionId,
        userId,
        type: blockType,
        ...(isTable
          ? null
          : typeof blockData.htmlContent === 'string'
          ? { htmlContent: blockData.htmlContent }
          : { htmlContent: JSON.stringify(blockData.htmlContent || {}) }),
        zIndex: blockConfig.z,
        position: {
          top_px: blockConfig.y,
          left_px: blockConfig.x,
        },
        dimensions: {
          width_px: blockConfig.width,
          height_px: blockConfig.height,
        },
        ...(isTable &&
          blockData.content && {
            content: blockData.content,
          }),
        ...(blockData.styles && {
          styles: cleanStylesForPayload(blockData.styles),
        }),
      };

      try {
        const response = await new Promise<GridAddedSocketResponseType>((resolve, reject) => {
          socketClient.publish(
            BlockEvents.BLOCK_ADDED,
            payload,
            (response: any) => {
              if (response.status === AcknowledgmentResponseStatus.OK || response.status === 'success') {
                resolve(response);
              } else {
                reject(new Error(response.errorCode || 'Block creation failed'));
              }
            },
            { force: true }
          );
        });

        dispatch(setSaveStatusBasedOnApiResponse(response));

        if (checkDocumentLockStatus(response.errorCode)) {
          throw new Error('Document locked');
        }

        const blockId = response.content.blockId;

        dispatch(
          addGridBlockState({
            sectionId,
            blockId,
            content: isTable ? '' : blockData.htmlContent || '',
            blockConfig: {
              ...blockConfig,
              id: blockId,
            },
            blockType,
          })
        );

        blockData.styles &&
          dispatch(
            updateBlockSettings({
              blockId,
              blockSettings: blockData.styles,
            })
          );

        return response;
      } catch (error) {
        console.error('Failed to add block:', error);
        dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        throw error;
      }
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );
}

/**
 * Clean styles for WebSocket payload
 * Removes properties not in AsyncAPI schema
 */
function cleanStylesForPayload(styles: BlockStyles): any {
  const { ...cleanStyles } = styles as any;

  return {
    borderLeft: cleanStyles.borderLeft ?? 0,
    borderRight: cleanStyles.borderRight ?? 0,
    borderTop: cleanStyles.borderTop ?? 0,
    borderBottom: cleanStyles.borderBottom ?? 0,
    borderRadius: cleanStyles.borderRadius ?? 0,
    paddingLeft: cleanStyles.paddingLeft ?? 0,
    paddingRight: cleanStyles.paddingRight ?? 0,
    paddingTop: cleanStyles.paddingTop ?? 0,
    paddingBottom: cleanStyles.paddingBottom ?? 0,
    ...(cleanStyles.backgroundColor && {
      backgroundColor: cleanStyles.backgroundColor,
    }),
    ...(cleanStyles.borderColor && {
      borderColor: cleanStyles.borderColor,
    }),
    ...(cleanStyles.imageAlt && { imageAlt: cleanStyles.imageAlt }),
    ...(cleanStyles.imageLink && { imageLink: cleanStyles.imageLink }),
    ...(cleanStyles.imageAspectRatioLock !== undefined && {
      imageAspectRatioLock: cleanStyles.imageAspectRatioLock,
    }),
    ...(cleanStyles.opacity !== undefined && {
      opacity: cleanStyles.opacity,
    }),
    ...(cleanStyles.lockedBy !== undefined && {
      lockedBy: cleanStyles.lockedBy,
    }),
    ...(cleanStyles.rotation !== undefined && {
      rotation: normalizeRotation(cleanStyles.rotation),
    }),
  };
}
